// OutflowDrawerContent — Drilldown fuer das 4W-Outflow-Hero-Tile.
//
// Spec (Kevin, Sprint-7-Push 3): „Nicht unterteilen nach Steuern und
// Rechnungen — alles zusammen, nur unter firmen unterteilen."
// Inhalt: Wochen-Buckets (W1..W4) als Leiste, dann pro Firma chronologische
// Liste der Posten (Datum + Vendor + Betrag + ggf. Rechnungsnr).

import {
  type FirmaBlock,
  type OutflowEntry,
  type OutflowForecastResponse,
  type WeeklyBucket,
  formatEURNegative,
} from "../../../lib/api";

interface Props {
  data?: OutflowForecastResponse | null;
  loading?: boolean;
}

const FIRMA_BADGE: Record<string, string> = {
  drainx: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
  gundk: "border-indigo-500/40 bg-indigo-500/10 text-indigo-200",
  infrax: "border-amber-500/40 bg-amber-500/10 text-amber-200",
  xholding: "border-sky-500/40 bg-sky-500/10 text-sky-200",
};

function shortDate(iso: string): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
  } catch {
    return iso;
  }
}

function fullDate(iso: string): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("de-DE", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function OutflowDrawerContent({ data, loading }: Props) {
  if (loading) {
    return (
      <div className="space-y-2 animate-pulse">
        <div className="h-12 rounded bg-slate-800/60" />
        <div className="h-32 rounded bg-slate-800/60" />
      </div>
    );
  }

  if (!data?.available) {
    return (
      <p className="text-slate-300 text-sm">
        {data?.setup_hint ?? "Outflow-Daten nicht verfügbar."}
      </p>
    );
  }

  // Nur Firmen mit 4W-Sum > 0 oder Posten zeigen, in fixer Reihenfolge.
  const order = ["drainx", "gundk", "infrax", "xholding", "unknown"];
  const byFirma = new Map(data.firmas.map((f) => [f.firma, f]));
  const firmas = order
    .map((id) => byFirma.get(id))
    .filter((f): f is FirmaBlock => !!f);

  return (
    <div className="space-y-5">
      {/* Konzern-Summary */}
      <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-4">
        <div className="text-[10px] uppercase tracking-widest text-rose-200/80">
          4-Wochen-Outflow Konzern
        </div>
        <div className="text-3xl font-light tabular-nums text-rose-200 mt-1">
          {formatEURNegative(data.next_28_sum_eur)}
        </div>
        <div className="text-xs text-slate-300 mt-1">
          {data.n_total} Posten · Stand {fullDate(data.as_of_date)}
        </div>
        {parseFloat(data.next_28_salary_sum_eur ?? "0") > 0 && (
          <div className="text-xs text-violet-300 tabular-nums mt-2">
            davon Gehaltszahlungen:{" "}
            {formatEURNegative(data.next_28_salary_sum_eur)}
          </div>
        )}
      </div>

      {/* Wochen-Buckets W1..W4 */}
      <WeekStrip buckets={data.weekly_buckets} />

      {/* Pro Firma — chronologisch */}
      <div className="space-y-4">
        {firmas.map((f) => (
          <FirmaSection key={f.firma} firma={f} />
        ))}
      </div>
    </div>
  );
}

function WeekStrip({ buckets }: { buckets: WeeklyBucket[] }) {
  if (!buckets || buckets.length === 0) return null;
  const maxSum = Math.max(...buckets.map((b) => parseFloat(b.sum_eur) || 0), 1);
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-2">
        Aufteilung nach Woche
      </div>
      <div className="grid grid-cols-4 gap-2">
        {buckets.map((b) => {
          const val = parseFloat(b.sum_eur) || 0;
          const heightPct = Math.round((val / maxSum) * 100);
          return (
            <div
              key={b.week_index}
              className="rounded-lg border border-slate-700/50 bg-slate-900/40 p-2 flex flex-col gap-1"
            >
              <div className="text-[10px] text-slate-400 uppercase tracking-wide">
                W{b.week_index} · {shortDate(b.start_iso)}–{shortDate(b.end_iso)}
              </div>
              <div className="text-base font-bold tabular-nums text-rose-200">
                {formatEURNegative(b.sum_eur)}
              </div>
              <div className="h-1 rounded-full bg-slate-800 overflow-hidden">
                <div
                  className="h-full bg-rose-400/60"
                  style={{ width: `${heightPct}%` }}
                />
              </div>
              <div className="text-[10px] text-slate-500 tabular-nums">
                {b.n_total} {b.n_total === 1 ? "Posten" : "Posten"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FirmaSection({ firma }: { firma: FirmaBlock }) {
  const badge =
    FIRMA_BADGE[firma.firma] ?? "border-slate-600 bg-slate-800/40 text-slate-200";
  // Drawer zeigt alle upcoming (Service liefert max 8 via _build_firma_blocks).
  const items = firma.upcoming;
  const salary = parseFloat(firma.next_28_salary_sum_eur ?? "0");
  const buckets = firma.weekly_buckets ?? [];
  return (
    <div className="rounded-xl border border-slate-700/40 bg-slate-950/60 p-3">
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span
            className={`text-[11px] px-2 py-0.5 rounded border font-semibold ${badge}`}
          >
            {firma.firma_display}
          </span>
          <span className="text-[11px] text-slate-400">
            {firma.n_total} Posten · 4W
          </span>
          {salary > 0 && (
            <span className="text-[10px] tabular-nums text-violet-300 px-2 py-0.5 rounded border border-violet-500/40 bg-violet-500/10">
              Gehaelter {formatEURNegative(firma.next_28_salary_sum_eur)}
            </span>
          )}
        </div>
        <div className="text-lg font-bold tabular-nums text-rose-200">
          {formatEURNegative(firma.next_28_sum_eur)}
        </div>
      </div>

      {/* Sprint-7-Push 9: Wochen-Spalten pro Firma — Kevin will mit den
          Zahlen arbeiten, nicht nur konsolidierte Summe sehen. */}
      {buckets.length > 0 && (
        <div className="grid grid-cols-4 gap-1.5 mb-3">
          {buckets.map((b) => (
            <div
              key={b.week_index}
              className="rounded bg-slate-900/60 border border-slate-700/40 p-1.5 text-center"
            >
              <div className="text-[9px] text-slate-500 uppercase tracking-wide">
                W{b.week_index}
              </div>
              <div className="text-xs font-semibold tabular-nums text-rose-200/90">
                {formatEURNegative(b.sum_eur)}
              </div>
              <div className="text-[9px] text-slate-500 tabular-nums">
                {b.n_total}
              </div>
            </div>
          ))}
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-xs text-slate-500">Keine offenen Posten.</p>
      ) : (
        <ul className="divide-y divide-slate-800/60">
          {items.map((e, i) => (
            <EntryRow key={i} e={e} />
          ))}
        </ul>
      )}
    </div>
  );
}

function EntryRow({ e }: { e: OutflowEntry }) {
  return (
    <li className="py-2 flex items-start gap-3">
      <div className="w-14 shrink-0 text-[11px] tabular-nums text-slate-400">
        {fullDate(e.outflow_date)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-100 truncate">
          {e.vendor_name}
        </div>
        {e.rechnungsnummer && (
          <div className="text-[10px] text-slate-500 tabular-nums">
            #{e.rechnungsnummer}
          </div>
        )}
      </div>
      <div className="tabular-nums text-rose-200 font-bold shrink-0">
        {formatEURNegative(e.betrag_eur)}
      </div>
    </li>
  );
}
